import { Badge, Table } from "@mantine/core";
import React, { useState, useEffect } from "react";
import InfoGridList from "./infoGridList";
import { useCellRender } from "../hooks/useCellRender";
import { useServerApi } from "../hooks/userServerApi";
import { useTranslation } from "react-i18next";
import { isClientClassEnabled } from "../helpers/clientClass";
import { isIdExpiryRequired } from "../helpers/clientIdentity";

export default function ClientBasicInfo({ form, section = "basic" }) {
  const [cellRender] = useCellRender();
  const client = form.values;
  const dataFontWeight = 600;
  const { t } = useTranslation();
  const emptyStr = "-";
  const clientType = client.type;
  const isIndividual = clientType === "INDIVIDUAL";

  const [api] = useServerApi();
  const [creditBalance, setCreditBalance] = useState(0);

  useEffect(() => {
    fetchCredit();
  }, [client]);

  const fetchCredit = async () => {
    try {
      if (!client || !client._id) return;
      const credit = await api.Client.getAvailableCredit(client._id);
      setCreditBalance(credit.balance);
    } catch (error) {
      console.log(error);
    }
  };

  const knowledgeOfDerivativesField = {
    title: "Investor with Knowledge of Derivatives",
    value: client.knowledgeOfDerivatives ? (
      <Badge size="sm" color="teal" radius="sm">
        Yes
      </Badge>
    ) : (
      emptyStr
    ),
  };

  const piFields = [
    {
      title: "Professional Investor",
      value: (
        <Badge size="sm" color="yellow" radius="sm">
          Yes
        </Badge>
      ),
    },
    {
      title: "PI Effective Date",
      value: cellRender.Date(client.piEffectiveDate, "YYYY-MM-DD") ?? emptyStr,
    },
    {
      title: "PI Annual Confirmation Date",
      value:
        cellRender.Date(client.piAnnualConfirmationDate, "YYYY-MM-DD") ??
        emptyStr,
    },
    {
      title: "PI Expiration Reminder Date",
      value:
        cellRender.Date(client.piExpirationReminderDate, "YYYY-MM-DD") ??
        emptyStr,
    },
    {
      title: "PI Termination Date",
      value: cellRender.Date(client.piTerminationDate, "YYYY-MM-DD") ?? emptyStr,
    },
  ];

  const basicInfoFields = [
    { title: "Code", value: client.code ?? emptyStr },
    { title: "Status", value: t(client.status) ?? emptyStr },
    ...(isClientClassEnabled()
      ? [{ title: "Client Class", value: client.clientClass || emptyStr }]
      : []),
    { title: "Client Type", value: t(client.type) ?? emptyStr },
    { title: "Name", value: client.name ?? emptyStr },
    { title: "Chinese Name", value: client.cname ?? emptyStr },
    ...(isIndividual
      ? [
          {
            title: "Created At",
            value: cellRender.Date(
              client.createdAt,
              "YYYY-MM-DD",
              dataFontWeight
            ),
          },
          {
            title: "Account Close Date",
            value:
              cellRender.Date(client.accountCloseDate, "YYYY-MM-DD") ??
              emptyStr,
          },
          {
            title: "Available Credit",
            value: cellRender.Currency(creditBalance) ?? emptyStr,
          },
          knowledgeOfDerivativesField,
        ]
      : []),
  ];

  const personalFields = [
    { title: "Gender", value: t(client.gender) ?? emptyStr },
    {
      title: "Date of Birth",
      value: cellRender.Date(client.birthday, "YYYY-MM-DD") ?? emptyStr,
    },
    {
      title: "Place of Birth",
      value: t(client.placeOfBirth) ?? emptyStr,
    },
    { title: "Nationality", value: t(client.nationality) ?? emptyStr },
    {
      title: "Marital Status",
      value: t(client.maritalStatus) ?? emptyStr,
    },
    { title: "Education", value: t(client.educationLevel) ?? emptyStr },
    { title: "Occupation", value: t(client.occupation) ?? emptyStr },
    { title: "Year Salary", value: client.yearlySalary ?? emptyStr },
  ];

  const companyFields = [
    {
      title: "Parent Company",
      value: client.parentCompany?.name ?? emptyStr,
    },
  ];

  const accountFields = [
    {
      title: "Created At",
      value: cellRender.Date(client.createdAt, "YYYY-MM-DD", dataFontWeight),
    },
    {
      title: "Account Close Date",
      value: cellRender.Date(client.accountCloseDate, "YYYY-MM-DD") ?? emptyStr,
    },
    {
      title: "Available Credit",
      value: cellRender.Currency(creditBalance) ?? emptyStr,
    },
    knowledgeOfDerivativesField,
  ];

  const identityFields = [
    { title: "ID Type", value: t(client.idType) ?? emptyStr },
    { title: "ID No.", value: client.id ?? emptyStr },
    {
      title: "ID Issue Country",
      value: t(client.idIssueCountry) ?? emptyStr,
    },
    {
      title: "ID Expiry",
      value: isIdExpiryRequired(client.idType)
        ? cellRender.Date(client.idDocExpiryDate, "YYYY-MM-DD") ?? emptyStr
        : emptyStr,
    },
  ];

  const otherIds = client.otherIds ?? [];

  const renderOtherIdsTable = () =>
    otherIds.length > 0 ? (
      <Table fontSize="xs" striped withBorder mt="xl">
        <thead>
          <tr>
            <th>{t("Other IDs")}</th>
            <th>{t("ID No.")}</th>
            <th>{t("Issue Country")}</th>
            <th>{t("Expiry")}</th>
          </tr>
        </thead>
        <tbody>
          {otherIds.map((row, index) => (
            <tr key={index}>
              <td>{t(row.idType) ?? emptyStr}</td>
              <td>{row.id ?? emptyStr}</td>
              <td>{t(row.idIssueCountry) ?? emptyStr}</td>
              <td>
                {isIdExpiryRequired(row.idType)
                  ? cellRender.Date(row.idDocExpiryDate, "YYYY-MM-DD") ??
                    emptyStr
                  : emptyStr}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    ) : null;

  if (section === "pi") {
    if (!client.professionalInvestor) return null;
    return <InfoGridList fields={piFields} values={client} cols={4} />;
  }

  if (section === "personal") {
    return <InfoGridList fields={personalFields} values={client} cols={4} />;
  }

  if (section === "company") {
    return (
      <>
        <InfoGridList fields={identityFields} values={client} cols={4} />
        {renderOtherIdsTable()}
        <InfoGridList fields={companyFields} values={client} cols={4} mt="xl" />
      </>
    );
  }

  if (section === "account") {
    return <InfoGridList fields={accountFields} values={client} cols={4} />;
  }

  if (section === "identity") {
    return (
      <>
        <InfoGridList fields={identityFields} values={client} cols={4} />
        {renderOtherIdsTable()}
      </>
    );
  }

  return <InfoGridList fields={basicInfoFields} values={client} cols={4} />;
}
